import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, ScrollView, TouchableOpacity, FlatList, Modal, TextInput, Alert, RefreshControl, Animated, Share } from 'react-native';
import tw from 'twrnc';

import HeaderBar from '../../../components/HeaderBar';
import { DSGradientBackground } from '../../../design-system/components/DSGradientBackground';
import { DSText } from '../../../design-system/components/DSText';
import { useAppTheme } from '../../../design-system/ThemeProvider';
import { spacing } from '../../../design-system/tokens';
import { t } from '../../../i18n/translations';
import { 
  evaluateAchievements,
  getAchievements,
  getUserAchievements,
  getAchievementCategoryEmoji,
  formatXP,
  type Achievement,
  type UserAchievement
} from '../../../shared/services/gamificationService';
import { isScreenshotMode, DEMO_ACHIEVEMENTS, DEMO_USER_ACHIEVEMENTS } from '../../../../lib/screenshotMode';
import { achievementText } from '../../../shared/services/achievementText';
import { useLanguageStore } from '../../quiz-core/languageStore';
import { useUserProfile } from '../../quiz-core/useStore';

const gt = (key: string, language: string) => t(`gamification.${key}`, language);

// Name/description come from the database in English; show the translated text when the app
// has it for this achievement_key.
const localizeAchievement = <T extends { achievement_key?: string; name: string; description: string }>(
  achievement: T,
  language: string,
): T => ({
  ...achievement,
  name: achievementText(achievement.achievement_key, 'name', achievement.name, language),
  description: achievementText(achievement.achievement_key, 'description', achievement.description, language),
});

// Category ids come from the data ('knowledge', 'streak', ...) — show the translated label and
// fall back to the raw id for a category the app has no label for.
const categoryLabel = (category: string, language: string) => {
  const key = `category_${category.toLowerCase()}`;
  const value = gt(key, language);
  return value === `gamification.${key}` ? category : value;
};

interface AchievementCardProps {
  achievement: Achievement;
  userAchievement?: UserAchievement;
  onPress: () => void;
}

interface AchievementModalProps {
  achievement: Achievement | null;
  userAchievement?: UserAchievement;
  visible: boolean;
  onClose: () => void;
  onShare: () => void;
}

const AchievementModal: React.FC<AchievementModalProps> = ({
  achievement,
  userAchievement,
  visible,
  onClose,
  onShare
}) => {
  const { language } = useLanguageStore();
  const g = (key: string) => gt(key, language);
  const scaleAnim = new Animated.Value(0);

  useEffect(() => {
    if (visible) {
      Animated.spring(scaleAnim, {
        toValue: 1,
        useNativeDriver: true,
        tension: 100,
        friction: 8,
      }).start();
    } else {
      scaleAnim.setValue(0);
    }
  }, [visible]);

  if (!achievement) return null;

  const isCompleted = userAchievement?.completed || false;
  const progress = userAchievement?.current_progress || 0;
  const progressPercentage = (progress / achievement.required_progress) * 100;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={tw`flex-1 bg-black bg-opacity-50 justify-center items-center p-4`}>
        <Animated.View 
          style={[
            tw`bg-[#080A18] rounded-2xl p-6 w-full max-w-sm shadow-2xl`,
            { transform: [{ scale: scaleAnim }] }
          ]}
        >
          {/* Header */}
          <View style={tw`items-center mb-6`}>
            <View style={tw`relative`}>
              <Text style={tw`text-6xl ${isCompleted ? '' : 'opacity-50'}`}>
                {achievement.icon}
              </Text>
              {isCompleted && (
                <View style={tw`absolute -top-2 -right-2 bg-[#1E7A28] rounded-full w-8 h-8 items-center justify-center`}>
                  <Text style={tw`text-white text-sm font-bold`}>✓</Text>
                </View>
              )}
            </View>
            
            <Text style={tw`text-xl font-bold text-center mt-4 ${isCompleted ? 'text-green-800' : 'text-yellow-50'}`}>
              {achievement.name}
            </Text>
            
            <View style={tw`flex-row items-center mt-2`}>
              <Text style={tw`text-lg font-semibold text-yellow-100 mr-1`}>
                {formatXP(achievement.xp_reward)}
              </Text>
              <Text style={tw`text-sm text-yellow-100`}>{g('xp_short')} {t('reward', language)}</Text>
            </View>
          </View>

          {/* Description */}
          <View style={tw`mb-6`}>
            <Text style={tw`text-base text-yellow-100 text-center leading-6`}>
              {achievement.description}
            </Text>
          </View>

          {/* Progress Section */}
          {achievement.required_progress > 1 && (
            <View style={tw`mb-6`}>
              <Text style={tw`text-sm font-semibold text-yellow-100 mb-3 text-center`}>
                {g('sort_by_progress')}
              </Text>
              
              <View style={tw`bg-[#141022] rounded-lg p-4`}>
                <View style={tw`flex-row justify-between items-center mb-2`}>
                  <Text style={tw`text-sm text-yellow-100`}>
                    {progress} / {achievement.required_progress}
                  </Text>
                  <Text style={tw`text-sm font-semibold ${isCompleted ? 'text-green-600' : 'text-yellow-100'}`}>
                    {Math.round(progressPercentage)}%
                  </Text>
                </View>
                
                <View style={tw`bg-[#2A2440] rounded-full h-3`}>
                  <View 
                    style={[
                      tw`${isCompleted ? 'bg-[#1E7A28]' : 'bg-[#101A32]0'} rounded-full h-3`,
                      { width: `${Math.min(progressPercentage, 100)}%` }
                    ]}
                  />
                </View>
                
                {!isCompleted && (
                  <Text style={tw`text-xs text-yellow-100 mt-2 text-center`}>
                    {achievement.required_progress - progress} {g('more_to_go')}
                  </Text>
                )}
              </View>
            </View>
          )}

          {/* Category and Completion Info */}
          <View style={tw`mb-6`}>
            <View style={tw`flex-row items-center justify-center mb-3`}>
              <View style={tw`bg-[#141022] rounded-full px-4 py-2`}>
                <Text style={tw`text-sm text-yellow-100 font-medium`}>
                  {getAchievementCategoryEmoji(achievement.category)} {categoryLabel(achievement.category, language)}
                </Text>
              </View>
            </View>
            
            {isCompleted && userAchievement?.completed_at && (
              <Text style={tw`text-sm text-green-600 text-center`}>
                {g('completed_on')} {new Date(userAchievement.completed_at).toLocaleDateString()}
              </Text>
            )}
          </View>

          {/* Action Buttons */}
          <View style={tw`flex-row space-x-3`}>
            <TouchableOpacity
              style={tw`flex-1 bg-[#100C20]0 rounded-lg py-3`}
              onPress={onClose}
            >
              <Text style={tw`text-white font-semibold text-center`}>
                {t('close', language)}
              </Text>
            </TouchableOpacity>
            
            {isCompleted && (
              <TouchableOpacity
                style={tw`flex-1 bg-[#101A32]0 rounded-lg py-3`}
                onPress={onShare}
              >
                <Text style={tw`text-white font-semibold text-center`}>
                  {t('share', language)}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
};

const AchievementCard: React.FC<AchievementCardProps> = ({ 
  achievement, 
  userAchievement, 
  onPress 
}) => {
  const { language } = useLanguageStore();
  const g = (key: string) => gt(key, language);
  const isCompleted = userAchievement?.completed || false;
  const progress = userAchievement?.current_progress || 0;
  const progressPercentage = (progress / achievement.required_progress) * 100;
  const remaining = Math.max(achievement.required_progress - progress, 0);

  return (
    <TouchableOpacity
      style={tw`rounded-3xl p-5 mb-4 shadow-sm ${isCompleted ? 'bg-[#052B18] border border-green-700' : 'bg-[#080A18] border border-yellow-700'}`}
      onPress={onPress}
    >
      <View style={tw`flex-row items-start`}>
        {/* Icon */}
        <View style={tw`mr-4`}>
          <View style={tw`${isCompleted ? 'bg-[#0A3D22]' : 'bg-[#141022]'} rounded-2xl px-3 py-3`}>
            <Text style={tw`text-4xl ${isCompleted ? '' : 'opacity-75'}`}>
              {achievement.icon}
            </Text>
          </View>
          {isCompleted && (
            <View style={tw`absolute -top-2 -right-2 bg-[#052B18]0 rounded-full w-7 h-7 items-center justify-center`}>
              <Text style={tw`text-white text-xs font-bold`}>✓</Text>
            </View>
          )}
        </View>

        {/* Content */}
        <View style={tw`flex-1`}>
          <View style={tw`flex-row items-start justify-between mb-2`}>
            <View style={tw`flex-1 pr-3`}>
              <Text style={tw`text-lg font-bold ${isCompleted ? 'text-yellow-50' : 'text-yellow-50'}`}>
                {achievement.name}
              </Text>
              <Text style={tw`text-sm text-yellow-100 mt-1`}>
                {achievement.description}
              </Text>
            </View>
            <View style={tw`${isCompleted ? 'bg-[#0A3D22]' : 'bg-[#28164A]'} rounded-full px-3 py-2`}>
              <Text style={tw`${isCompleted ? 'text-yellow-100' : 'text-yellow-100'} text-xs font-bold`}>
                +{achievement.xp_reward} {g('xp_short')}
              </Text>
            </View>
          </View>

          {/* Progress Bar */}
          {achievement.required_progress > 1 && (
            <View style={tw`mb-3 mt-1`}>
              <View style={tw`flex-row justify-between items-center mb-2`}>
                <Text style={tw`text-xs uppercase tracking-wide text-yellow-100`}>
                  {g('sort_by_progress')}
                </Text>
                <Text style={tw`text-xs font-semibold ${isCompleted ? 'text-yellow-100' : 'text-yellow-100'}`}>
                  {progress} / {achievement.required_progress}
                </Text>
              </View>
              <View style={tw`bg-[#2A2440] rounded-full h-2.5 overflow-hidden`}>
                <View 
                  style={[
                    tw`${isCompleted ? 'bg-[#052B18]0' : 'bg-violet-500'} rounded-full h-2.5`,
                    { width: `${Math.min(progressPercentage, 100)}%` }
                  ]}
                />
              </View>
              {!isCompleted && (
                <Text style={tw`text-xs text-yellow-100 mt-2`}>
                  {remaining} {g('more_to_go')}
                </Text>
              )}
            </View>
          )}

          {/* Category Badge */}
          <View style={tw`flex-row items-center justify-between`}>
            <View style={tw`${isCompleted ? 'bg-[#0A3D22]' : 'bg-[#141022]'} rounded-full px-3 py-1.5`}>
              <Text style={tw`text-xs ${isCompleted ? 'text-yellow-100' : 'text-yellow-100'} capitalize font-medium`}>
                {getAchievementCategoryEmoji(achievement.category)} {categoryLabel(achievement.category, language)}
              </Text>
            </View>
            
            {isCompleted && userAchievement?.completed_at ? (
              <Text style={tw`text-xs text-yellow-100 font-medium`}>
                {g('completed')} {new Date(userAchievement.completed_at).toLocaleDateString()}
              </Text>
            ) : achievement.required_progress === 1 ? (
              <Text style={tw`text-xs text-yellow-100 font-medium`}>
                {isCompleted ? `${g('earned')} 100%` : g('ready_to_earn')}
              </Text>
            ) : null}
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );
};

export const AchievementsScreen: React.FC = () => {
  const { colors } = useAppTheme();
  const { id: userId, fetchProfile } = useUserProfile();
  const { language } = useLanguageStore();
  const g = (key: string) => gt(key, language);
  
  // Store screenshot capture: seed demo achievements, skip network + realtime
  const screenshotMode = isScreenshotMode();
  const [rawAchievements, setAchievements] = useState<Achievement[]>(
    screenshotMode ? (DEMO_ACHIEVEMENTS as Achievement[]) : []
  );
  const achievements = React.useMemo(
    () => rawAchievements.map((item) => localizeAchievement(item, language)),
    [rawAchievements, language],
  );
  const [userAchievements, setUserAchievements] = useState<UserAchievement[]>(
    screenshotMode ? (DEMO_USER_ACHIEVEMENTS as UserAchievement[]) : []
  );
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(!screenshotMode);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'progress' | 'xp' | 'completed'>('name');
  const [selectedAchievement, setSelectedAchievement] = useState<Achievement | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const categories = ['all', 'milestone', 'knowledge', 'mastery', 'consistency', 'community'] as const;
  type AchievementSort = 'name' | 'progress' | 'xp' | 'completed';

  useEffect(() => {
    if (screenshotMode) return;
    void fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (!screenshotMode && userId) {
      loadAchievements();
      setupRealtimeSubscription();
    }
    
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, [userId]);

  // Real-time subscription
  const subscriptionRef = React.useRef<{ unsubscribe: () => void } | null>(null);

  const setupRealtimeSubscription = () => {
    if (!userId) return;

    // Clean up existing subscription
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
    }

    // Import and setup subscription
    import('../../../shared/services/gamificationService').then(({ subscribeToGamificationUpdates }) => {
      subscriptionRef.current = subscribeToGamificationUpdates(userId, {
        onAchievementUpdate: (newAchievements) => {
          setUserAchievements(newAchievements);
          
          // Check for newly unlocked achievements
          const newlyUnlocked = newAchievements.filter(
            achievement => achievement.unlocked_at && 
            Date.now() - new Date(achievement.unlocked_at).getTime() < 5000 // Within last 5 seconds
          );
          
          if (newlyUnlocked.length > 0) {
            // Show celebration for new achievements
            newlyUnlocked.forEach(achievement => {
              Alert.alert(
                g('achievement_unlocked'),
                achievement.title,
                [{ text: t('ok', language) }]
              );
            });
          }
        },
        onError: (error) => {
          console.error('Achievement subscription error:', error);
          setError(g('achievements_connection_error'));
        }
      });
    });
  };

  const loadAchievements = useCallback(async (isRefresh = false) => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      // Bring progress up to date from play history before reading it back.
      await evaluateAchievements(userId);
      const [allAchievements, userAchievementData] = await Promise.all([
        getAchievements(),
        getUserAchievements(userId)
      ]);

      setAchievements(allAchievements);
      setUserAchievements(userAchievementData);
    } catch (error) {
      console.error('Error loading achievements:', error);
      setError(g('achievements_error'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [userId, language]);

  const onRefresh = useCallback(() => {
    loadAchievements(true);
  }, [loadAchievements]);

  const getFilteredAchievements = () => {
    let filtered = achievements;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(achievement => achievement.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(achievement => 
        achievement.name.toLowerCase().includes(query) ||
        achievement.description.toLowerCase().includes(query) ||
        achievement.category.toLowerCase().includes(query)
      );
    }

    // Sort achievements
    filtered.sort((a, b) => {
      const userAchievementA = getUserAchievementForId(a.id);
      const userAchievementB = getUserAchievementForId(b.id);

      switch (sortBy) {
        case 'progress': {
          const progressA = (userAchievementA?.current_progress || 0) / a.required_progress;
          const progressB = (userAchievementB?.current_progress || 0) / b.required_progress;
          return progressB - progressA;
        }
        
        case 'xp':
          return b.xp_reward - a.xp_reward;
        
        case 'completed': {
          const completedA = userAchievementA?.completed ? 1 : 0;
          const completedB = userAchievementB?.completed ? 1 : 0;
          return completedB - completedA;
        }
        
        case 'name':
        default:
          return a.name.localeCompare(b.name);
      }
    });

    return filtered;
  };

  const getUserAchievementForId = (achievementId: string) => {
    return userAchievements.find(ua => ua.achievement_id === achievementId);
  };

  const getCompletedCount = () => {
    return userAchievements.filter(ua => ua.completed).length;
  };

  const getCategoryCount = (category: string) => {
    if (category === 'all') return achievements.length;
    return achievements.filter(a => a.category === category).length;
  };

  const getCategoryCompletedCount = (category: string) => {
    const categoryAchievements = category === 'all' 
      ? achievements 
      : achievements.filter(a => a.category === category);
    
    return categoryAchievements.filter(a => {
      const userAchievement = getUserAchievementForId(a.id);
      return userAchievement?.completed;
    }).length;
  };

  const handleAchievementPress = (achievement: Achievement) => {
    setSelectedAchievement(achievement);
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedAchievement(null);
  };

  const handleShareAchievement = async () => {
    if (!selectedAchievement) return;

    const userAchievement = getUserAchievementForId(selectedAchievement.id);
    if (!userAchievement?.completed) return;

    try {
      const message = `🏆 I just unlocked the "${selectedAchievement.name}" achievement in Kingdom Bible Quiz! ${selectedAchievement.icon}\n\n"${selectedAchievement.description}"\n\n+${formatXP(selectedAchievement.xp_reward)} ${t('gamification.xp_short', language)} earned! 🎉`;
      
      await Share.share({
        message,
        title: `Achievement Unlocked: ${selectedAchievement.name}`
      });
    } catch (error) {
      console.error('Error sharing achievement:', error);
      Alert.alert(
        t('error', language),
        t('share_failed', language),
        [{ text: t('ok', language) }]
      );
    }
  };

  if (loading && achievements.length === 0) {
    return (
      <DSGradientBackground preset="surface">
        <HeaderBar title={g('achievements')} variant="simple" showProfile={false} showClose={false} />
        <View style={tw`flex-1 justify-center items-center`}>
          <Text style={tw`text-lg text-yellow-100`}>
            {t('loading', language)}...
          </Text>
        </View>
      </DSGradientBackground>
    );
  }

  if (error && achievements.length === 0) {
    return (
      <DSGradientBackground preset="surface">
        <HeaderBar title={g('achievements')} variant="simple" showProfile={false} showClose={false} />
        <View style={tw`flex-1 justify-center items-center p-4`}>
          <Text style={tw`text-xl mb-2`}>⚠️</Text>
          <Text style={tw`text-lg text-yellow-50 mb-4 text-center`}>
            {g('achievements_error')}
          </Text>
          <TouchableOpacity
            style={tw`bg-[#101A32]0 rounded-lg py-3 px-6`}
            onPress={() => loadAchievements()}
          >
            <Text style={tw`text-white font-semibold`}>
              {t('retry', language)}
            </Text>
          </TouchableOpacity>
        </View>
      </DSGradientBackground>
    );
  }

  const filteredAchievements = getFilteredAchievements();
  const completedCount = getCompletedCount();
  const totalCount = achievements.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const renderHeader = () => (
    <>
      <View
        style={[
          tw`mx-4 mt-4 rounded-[28px] p-6`,
          {
            backgroundColor: colors.white,
            borderWidth: 1,
            borderColor: colors.outlineVariant,
            shadowColor: colors.black,
            shadowOpacity: 0.05,
            shadowRadius: 18,
            shadowOffset: { width: 0, height: 8 },
            elevation: 3,
          },
        ]}
      >
        <View style={tw`flex-row items-center justify-between mb-3`}>
          <View
            style={[
              tw`rounded-full px-3 py-2`,
              { backgroundColor: colors.accentContainer },
            ]}
          >
            <DSText variant="micro" uppercase color={colors.onAccentContainer} style={{ fontWeight: '700' }}>
              {g('achievements')}
            </DSText>
          </View>
          <View
            style={[
              tw`rounded-full px-3 py-2`,
              { backgroundColor: colors.surfaceContainerLow, borderWidth: 1, borderColor: colors.outlineVariant },
            ]}
          >
            <DSText variant="micro" color={colors.onSurfaceVariant}>
              {getCompletedCount()} / {achievements.length}
            </DSText>
          </View>
        </View>

        <DSText variant="title" color={colors.onSurface} style={{ marginBottom: spacing.sm, fontSize: 20 }}>
          {g('achievements_hero_title')}
        </DSText>
        <DSText variant="body" color={colors.onSurfaceVariant} style={{ marginBottom: spacing.lg }}>
          {g('achievements_hero_subtitle')}
        </DSText>

        <View style={tw`flex-row mb-3`}>
          <View style={[tw`flex-1 rounded-2xl px-4 py-4 mr-2`, { backgroundColor: colors.correctContainer, borderWidth: 1, borderColor: colors.correctLight }]}>
            <DSText variant="caption" color={colors.correctDark} style={{ marginBottom: spacing.xs, fontWeight: '600' }}>
              {g('completed')}
            </DSText>
            <DSText variant="heading" color={colors.correct}>
              {completedCount}
            </DSText>
          </View>

          <View style={[tw`flex-1 rounded-2xl px-4 py-4 ml-2`, { backgroundColor: colors.primaryContainer, borderWidth: 1, borderColor: colors.primaryLight }]}>
            <DSText variant="caption" color={colors.primary} style={{ marginBottom: spacing.xs, fontWeight: '600' }}>
              {g('total')}
            </DSText>
            <DSText variant="heading" color={colors.primary}>
              {totalCount}
            </DSText>
          </View>
        </View>

        <View style={[tw`rounded-2xl px-4 py-4`, { backgroundColor: colors.surfaceContainerLow, borderWidth: 1, borderColor: colors.outlineVariant }]}>
          <View style={tw`flex-row items-center justify-between mb-2`}>
            <DSText variant="caption" color={colors.onSurfaceVariant} style={{ fontWeight: '600' }}>
              {g('sort_by_progress')}
            </DSText>
            <DSText variant="label" color={colors.onSurface}>
              {progressPercent}%
            </DSText>
          </View>
          <View style={tw`bg-[#2A2440] rounded-full h-2.5 overflow-hidden`}>
            <View
              style={[
                tw`bg-violet-500 rounded-full h-2.5`,
                { width: `${progressPercent}%` },
              ]}
            />
          </View>
          <DSText variant="caption" color={colors.onSurfaceVariant} style={{ marginTop: spacing.sm }}>
            {completedCount} / {Math.max(totalCount, 1)} {g('achievements').toLowerCase()}
          </DSText>
        </View>
      </View>

      <View style={tw`mx-4 mt-4`}>
        <View
          style={[
            tw`bg-[#080A18] rounded-2xl px-4 py-3 mb-3 shadow-sm border flex-row items-center`,
            {
              borderColor: isSearchFocused ? colors.primary : colors.outlineVariant,
              shadowColor: isSearchFocused ? colors.primary : colors.black,
              shadowOpacity: isSearchFocused ? 0.12 : 0.04,
              shadowRadius: isSearchFocused ? 10 : 6,
              shadowOffset: { width: 0, height: 4 },
              elevation: isSearchFocused ? 4 : 2,
            }
          ]}
        >
          <Text style={tw`mr-3 text-lg text-slate-400`}>🔎</Text>
          <TextInput
            style={tw`flex-1 text-base text-yellow-50`}
            placeholder={g('search_achievements')}
            placeholderTextColor="#9CA3AF"
            value={searchQuery}
            onChangeText={setSearchQuery}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
            returnKeyType="search"
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity
              onPress={() => setSearchQuery('')}
              accessibilityRole="button"
              accessibilityLabel={t('clear', language)}
              style={tw`ml-3`}
            >
              <Text style={tw`text-sm font-semibold text-yellow-100`}>
                {t('clear', language)}
              </Text>
            </TouchableOpacity>
          )}
        </View>

        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={tw`pb-3`}>
          {[
            { key: 'name', label: g('sort_by_name') },
            { key: 'progress', label: g('sort_by_progress') },
            { key: 'xp', label: g('sort_by_xp') },
            { key: 'completed', label: g('sort_by_completed') }
          ].map((option) => (
            <TouchableOpacity
              key={option.key}
              style={tw`mr-3 px-4 py-2 rounded-full ${
                sortBy === option.key
                  ? 'bg-slate-900'
                  : 'bg-[#080A18] border border-yellow-700'
              }`}
              onPress={() => setSortBy(option.key as AchievementSort)}
            >
              <Text style={tw`text-xs ${
                sortBy === option.key
                  ? 'text-white font-semibold'
                  : 'text-yellow-100'
              }`}>
                {option.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={tw`px-4 mb-4`}
      >
        {categories.map((category) => (
          <TouchableOpacity
            key={category}
            style={tw`mr-3 px-4 py-2 rounded-full ${
              selectedCategory === category
                ? 'bg-slate-900'
                : 'bg-[#080A18] border border-yellow-700'
            }`}
            onPress={() => setSelectedCategory(category)}
          >
            <Text style={tw`text-xs ${
              selectedCategory === category
                ? 'text-white font-semibold'
                : 'text-yellow-100'
            } capitalize`}>
              {getAchievementCategoryEmoji(category)} {category === 'all' ? gt('category_all', language) : categoryLabel(category, language)} ({getCategoryCompletedCount(category)}/{getCategoryCount(category)})
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </>
  );

  return (
    <DSGradientBackground preset="surface">
      <HeaderBar title={g('achievements')} variant="simple" showProfile={false} showClose={false} />
      <FlatList
        data={filteredAchievements}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <AchievementCard
            achievement={item}
            userAchievement={getUserAchievementForId(item.id)}
            onPress={() => handleAchievementPress(item)}
          />
        )}
        ListHeaderComponent={renderHeader}
        ListEmptyComponent={
          <View style={tw`items-center justify-center py-12 px-6`}>
            <Text style={tw`text-6xl mb-4`}>
              {searchQuery ? '🔍' : '🏆'}
            </Text>
            <Text style={tw`text-lg font-semibold text-yellow-100 mb-2 text-center`}>
              {searchQuery ? g('no_search_results') : g('no_achievements')}
            </Text>
            <Text style={tw`text-sm text-yellow-100 text-center`}>
              {searchQuery ? g('try_different_search') : g('no_achievements_description')}
            </Text>
          </View>
        }
        contentContainerStyle={{ paddingBottom: spacing.xxxl }}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={['#3B82F6']}
            tintColor="#3B82F6"
          />
        }
        ListFooterComponent={<View style={{ height: spacing.xl }} />}
      />

      <AchievementModal
        achievement={selectedAchievement}
        userAchievement={selectedAchievement ? getUserAchievementForId(selectedAchievement.id) : undefined}
        visible={modalVisible}
        onClose={handleCloseModal}
        onShare={handleShareAchievement}
      />
    </DSGradientBackground>
  );
};
